import {Accordion} from '@mantine/core';
import {Box} from 'src/design/atoms/Box';
import {Grid} from 'src/design/atoms/Grid';
import React, {useCallback} from 'react';
import {useLocalStorage} from 'usehooks-ts';
import {SectionCard} from 'src/design/atoms/SectionCard';
import {useGetFormSettings} from '../hooks/useFormBuilder';
import type {FormBuilderRegistry} from '../registry';
import {FormBuilderRegistryProvider} from '../registry';
import {FieldList} from './FieldList';
import {FieldSettings} from './FieldSettings/FieldSettings';
import {FormPreview} from './FormPreview';
import {FormSettings} from './FormSettings';

export interface FormBuilderContentProps {
  registry: FormBuilderRegistry;
}

const FormBuilderContentInner: React.FC<{showFormSettings: boolean}> = ({
  showFormSettings,
}) => {
  const [expanded, setExpanded] = useLocalStorage<string | null>(
    'form_settings_expanded',
    'form-settings'
  );

  const revealFormSettings = useCallback(() => {
    setExpanded('form-settings');
  }, [setExpanded]);

  const formSettings = useGetFormSettings();
  const title = formSettings.title;

  return (
    <Box mb="md">
      {showFormSettings && (
        <Accordion
          value={expanded}
          mb="md"
          variant="contained"
          onChange={setExpanded}
          multiple={false}>
          <Accordion.Item value="form-settings">
            <Accordion.Control>{`Form Settings — ${
              title || 'Untitled'
            }`}</Accordion.Control>
            <Accordion.Panel>
              <FormSettings reveal={revealFormSettings} />
            </Accordion.Panel>
          </Accordion.Item>
        </Accordion>
      )}

      <Grid gutter="md">
        <Grid.Col span={{base: 12, lg: 3}}>
          <SectionCard title="Fields" data-testid="section-fields">
            <Box p="md">
              <FieldList />
            </Box>
          </SectionCard>
        </Grid.Col>
        <Grid.Col span={{base: 12, lg: 3}}>
          <SectionCard
            title="Field Settings"
            data-testid="section-field-settings">
            <Box p="md" bg="gray.0">
              <FieldSettings />
            </Box>
          </SectionCard>
        </Grid.Col>
        <Grid.Col span={{base: 12, lg: 6}}>
          <SectionCard title="Preview" data-testid="section-preview">
            <Box p="md">
              <FormPreview />
            </Box>
          </SectionCard>
        </Grid.Col>
      </Grid>
    </Box>
  );
};

export const FormBuilderContent: React.FC<FormBuilderContentProps> = ({
  registry,
}) => (
  <FormBuilderRegistryProvider registry={registry}>
    <FormBuilderContentInner showFormSettings={registry.showFormSettings} />
  </FormBuilderRegistryProvider>
);
